import React, { useState, useEffect } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { red, green, white } from '@edulastic/colors'

// matches the default assessment player header height so the banner
// covers the student navigation bar while offline
const BANNER_HEIGHT = 62

const OfflineNotifier = () => {
  const [isOffline, setOffline] = useState(false)
  const [isBackOnline, setIsBackOnline] = useState(false)

  useEffect(() => {
    let timer
    const handleOffline = () => {
      clearTimeout(timer)
      setIsBackOnline(false)
      setOffline(true)
    }
    const handleOnline = () => {
      setIsBackOnline(true)
      timer = setTimeout(() => {
        setOffline(false)
        setIsBackOnline(false)
      }, 2000)
    }
    window.addEventListener('offline', handleOffline)
    window.addEventListener('online', handleOnline)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('offline', handleOffline)
      window.removeEventListener('online', handleOnline)
    }
  }, [])

  if (!isOffline) return null

  return isBackOnline ? (
    <ConnectedAlert role="status">Connected</ConnectedAlert>
  ) : (
    <NoInternetAlert role="alert" data-cy="offline-banner">
      <Spinner aria-hidden="true">
        {[...new Array(12)].map((_, i) => (
          <div key={i} />
        ))}
      </Spinner>
      <span>
        No internet connection. Your work is not saving. Please wait until the
        internet connection has been restored.
      </span>
    </NoInternetAlert>
  )
}

export default OfflineNotifier

const slideDown = keyframes`
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(0);
  }
`

const NoInternetAlert = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  min-height: ${BANNER_HEIGHT}px;
  padding: 8px 20px;
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background-color: ${red};
  color: ${white};
  font-size: 15px;
  font-weight: bold;
  text-align: center;
  animation: ${slideDown} 0.25s ease-in-out 0s;

  @media print {
    display: none;
  }
`

const ConnectedAlert = styled(NoInternetAlert)`
  background-color: ${green};
`

const fade = keyframes`
  0% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
`

const Spinner = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  div {
    position: absolute;
    top: 0;
    left: 8px;
    width: 2px;
    height: 5px;
    border-radius: 1px;
    background: ${white};
    transform-origin: 1px 9px;
    animation: ${fade} 1.2s linear infinite;
  }
  ${[...new Array(12)].map(
    (_, i) => css`
      div:nth-child(${i + 1}) {
        transform: rotate(${i * 30}deg);
        animation-delay: ${(i - 11) * 0.1}s;
      }
    `
  )}
`
